import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Pressable, StyleSheet, View } from 'react-native'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { getTournamentMaxReentries } from '@ppwr/convex/shared/tournamentConstants'
import { describeMaxRebuys } from '@ppwr/convex/games/rebuyRules'

import { DS } from '@/design-system'
import { useStore } from '@/models'
import { ToggleRow } from '@/components/layouts/ToggleRow'
import { color } from '../../theme'
import { translate } from '../../i18n'
import { DrawerColorDots } from '../../components/ANewDesign/DrawerColorDots/DrawerColorDots'
import { useGameContext } from './GameContext'
import { useGameState } from './useGameState'

const PLACEHOLDER = '—'

/** Turn clock and blind-increase timers are stored in milliseconds on the game doc. */
const formatStartingChips = (chips: number | null | undefined): string =>
  typeof chips === 'number' ? chips.toLocaleString('en-US') : PLACEHOLDER

const formatBlinds = (smallBlind?: number, bigBlind?: number): string =>
  smallBlind != null && bigBlind != null ? `${smallBlind}/${bigBlind}` : PLACEHOLDER

const formatTurnClock = (turnDurationMs?: number): string =>
  typeof turnDurationMs === 'number' ? `${Math.round(turnDurationMs / 1000)} sec` : PLACEHOLDER

const formatBlindIncreases = (timeBetweenBlindsIncreaseMs?: number): string =>
  timeBetweenBlindsIncreaseMs ? `${Math.round(timeBetweenBlindsIncreaseMs / 60000)} min` : translate('common.off')

const formatRebuySetting = (maxRebuys: number | null | undefined): string => {
  const setting = describeMaxRebuys(maxRebuys)
  if (setting.kind === 'none') return translate('gameDetails.rebuyNone')
  if (setting.kind === 'unlimited') return translate('gameDetails.rebuyUnlimited')
  return String(setting.count)
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <View style={styles.row}>
    <DS.Text variant="Body Medium/Regular" color="Global/Black">
      {label}
    </DS.Text>
    <DS.Text variant="Body Medium/Medium" color="Global/Black">
      {value}
    </DS.Text>
  </View>
)

type TournamentStatusShape = {
  config: { startingChips: number; blindIncreaseIntervalMs: number; maxReentries?: number; maxRebuys?: number }
  currentSmallBlind: number
  currentBigBlind: number
}

/** Placeholder shown for tournament rows while the status query is loading. */
const TOURNAMENT_LOADING_VALUE = '-'

/**
 * Compute the settings row values. Tournament tables read blinds, starting
 * chips, blind interval, and re-entry count from the tournament (the game doc
 * does not carry them); cash tables read the game doc as before. While a
 * tournament table's status query is still loading, render placeholders rather
 * than flashing the cash-game values (tournament game docs carry no blinds).
 */
function getSettingsRowValues(args: {
  isTournament: boolean
  tournamentStatus: TournamentStatusShape | null | undefined
  startingChips: number | null | undefined
  game: { smallBlind?: number; bigBlind?: number; timeBetweenBlindsIncrease?: number } | null | undefined
  maxRebuys: number | null | undefined
}) {
  const { isTournament, tournamentStatus, startingChips, game, maxRebuys } = args
  if (isTournament && !tournamentStatus) {
    return {
      startingChipsValue: TOURNAMENT_LOADING_VALUE,
      blindsValue: TOURNAMENT_LOADING_VALUE,
      blindIncreasesValue: TOURNAMENT_LOADING_VALUE,
      rebuyRowLabel: translate('gameDetails.reentries'),
      rebuyRowValue: TOURNAMENT_LOADING_VALUE
    }
  }
  const maxReentries = getTournamentMaxReentries(tournamentStatus?.config ?? {})
  return {
    startingChipsValue: formatStartingChips(tournamentStatus ? tournamentStatus.config.startingChips : startingChips),
    blindsValue: tournamentStatus
      ? formatBlinds(tournamentStatus.currentSmallBlind, tournamentStatus.currentBigBlind)
      : formatBlinds(game?.smallBlind, game?.bigBlind),
    blindIncreasesValue: tournamentStatus
      ? formatBlindIncreases(tournamentStatus.config.blindIncreaseIntervalMs)
      : formatBlindIncreases(game?.timeBetweenBlindsIncrease),
    rebuyRowLabel: tournamentStatus ? translate('gameDetails.reentries') : translate('gameDetails.rebuy'),
    rebuyRowValue: tournamentStatus
      ? maxReentries > 0
        ? String(maxReentries)
        : translate('gameDetails.group.off')
      : formatRebuySetting(maxRebuys)
  }
}

/**
 * In-game settings drawer panel. Most rows are read-only game configuration;
 * card colors and teacher mode are interactive preferences/role switches.
 */
export const GameSettingsPanel = observer(({ onBack }: { onBack: () => void }) => {
  const store = useStore()
  const { userId } = useGameContext()
  const { game, players, startingChips, maxRebuys, isTeacherSeated, myPlayer, teacher } = useGameState()
  const {
    session: { isTeacher, user }
  } = store
  const isFourColoredDeckEnabled = store.game?.isFourColoredDeckEnabled ?? false

  // Tournament tables don't carry blind/starting-chip config on the game doc —
  // blinds advance on the tournament's blind clock. Read the tournament instead.
  const tournamentId = game?.tournamentId
  const tournamentStatus = useQuery(
    api.tournaments.queries.getTournamentStatus,
    tournamentId ? { tournamentId } : 'skip'
  )

  const { startingChipsValue, blindsValue, blindIncreasesValue, rebuyRowLabel, rebuyRowValue } = getSettingsRowValues({
    isTournament: !!tournamentId,
    tournamentStatus,
    startingChips,
    game,
    maxRebuys
  })
  const botCount = players.filter((p) => p.kind === 'bot').length

  const switchToTeacherSeat = useMutation(api.games.teacherSeat.switchToTeacherSeat)
  const switchToPlayerSeat = useMutation(api.games.teacherSeat.switchToPlayerSeat)
  const joinAsTeacher = useMutation(api.games.teacherSeat.joinAsTeacher)
  const [isTogglingTeacher, setIsTogglingTeacher] = useState(false)

  const isSoloMode = game?.mode === 'practice' || game?.mode === 'daily10'
  const showTeacherToggle = isTeacher && !isSoloMode && Boolean(game)
  const teacherSeatTakenByOther = Boolean(teacher && !isTeacherSeated)

  const handleTeacherModeChange = async (enabled: boolean) => {
    if (!game || isTogglingTeacher) return

    setIsTogglingTeacher(true)
    const userName = user?.name ?? 'Teacher'
    const mongoUserId = user?._id?.toString()

    try {
      if (enabled) {
        // Atomic seat→teacher switch so leaveGame never auto-ends a bots-only table.
        if (myPlayer) {
          await switchToTeacherSeat({ gameId: game._id, userId, name: userName })
        } else {
          await joinAsTeacher({ gameId: game._id, userId, name: userName })
        }
      } else {
        await switchToPlayerSeat({ gameId: game._id, userId, mongoUserId, name: userName })
      }
    } catch (error) {
      console.error('Failed to toggle teacher mode:', error)
    } finally {
      setIsTogglingTeacher(false)
    }
  }

  return (
    <View style={styles.container}>
      <Pressable onPress={onBack} hitSlop={8} style={styles.backButton}>
        <DS.Icon name="Arrow---left" size={24} color="Global/Black" />
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('common.back')}
        </DS.Text>
      </Pressable>

      <DS.Text variant="Caption/Bold" color="Global/Black" style={styles.heading}>
        {translate('gameDetails.title')}
      </DS.Text>

      <View style={styles.divider} />

      <View style={styles.rows}>
        {/* Card Colors — interactive display preference (4-color deck), not a locked game setting */}
        <View style={styles.row}>
          <DS.Text variant="Body Medium/Regular" color="Global/Black">
            {translate('gameDetails.cardColors')}
          </DS.Text>
          <DrawerColorDots
            initialSelectedIndex={isFourColoredDeckEnabled ? 0 : 1}
            onChangeColorIndex={(index) => store.game.setIsFourColoredDeckEnabled(index === 0)}
          />
        </View>
        <DetailRow label={translate('gameDetails.numberOfBots')} value={String(botCount)} />
        <DetailRow label={translate('gameDetails.startingChips')} value={startingChipsValue} />
        <DetailRow label={translate('gameDetails.blinds')} value={blindsValue} />
        <DetailRow label={translate('gameDetails.turnClock')} value={formatTurnClock(game?.turnDuration)} />
        <DetailRow label={translate('gameDetails.blindIncreases')} value={blindIncreasesValue} />
        <DetailRow label={rebuyRowLabel} value={rebuyRowValue} />
        {showTeacherToggle && (
          <ToggleRow
            label={translate('gameDetails.teacherMode')}
            value={isTeacherSeated}
            onValueChange={handleTeacherModeChange}
            disabled={isTogglingTeacher || teacherSeatTakenByOther}
            style={styles.teacherModeRow}
          />
        )}
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: DS.spacing[16]
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DS.spacing[8],
    paddingVertical: DS.spacing[16]
  },
  heading: {
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'right',
    marginTop: DS.spacing[8],
    marginBottom: DS.spacing[16]
  },
  divider: {
    height: 1,
    backgroundColor: color.palette.lightGrey
  },
  rows: {
    paddingTop: DS.spacing[20],
    gap: DS.spacing[20]
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  teacherModeRow: {
    paddingVertical: 0
  }
})
